import React, {useEffect, useRef, useState} from 'react';
import VariationPicker from '../VariationPicker';
import DetailsLoadingOverlay from '../DetailsLoadingOverlay';
import {getProductPrice} from '../modeStorage';
import {matchProductQuery} from '../useProductFilter';
import * as S from '../styles';
import {ProductPickResult, ProductPickerItem, ProductPickerVariation} from '../types';
import {parseQrCode} from '../../../utils/parseQrCode';
import {resolveProductDetails, willFetchProductDetails} from '../resolveProductDetails';

interface Props {
    products: ProductPickerItem[];
    disabled?: boolean;
    loading?: boolean;
    placeholder?: string;
    autoFocus?: boolean;
    compact?: boolean;
    onPick: (result: ProductPickResult) => void;
    fetchProductDetails?: (productId: number) => Promise<ProductPickerItem | null>;
    forceProductDetails?: boolean;
}

const PosScanMode = ({
    products,
    disabled,
    loading,
    placeholder = 'Сканер / артикул / ID — Enter',
    autoFocus = true,
    compact,
    onPick,
    fetchProductDetails,
    forceProductDetails,
}: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [focused, setFocused] = useState(false);
    const [query, setQuery] = useState('');
    const [message, setMessage] = useState('Ожидание сканера…');
    const [pendingProduct, setPendingProduct] = useState<ProductPickerItem | null>(null);
    const [pendingVariation, setPendingVariation] = useState<ProductPickerVariation | null>(null);
    const [pendingMeta, setPendingMeta] = useState<{qrWeight?: number; qrPrice?: number}>({});
    const [resolvingProduct, setResolvingProduct] = useState<ProductPickerItem | null>(null);

    useEffect(() => {
        if (autoFocus && !pendingProduct) {
            inputRef.current?.focus();
        }
    }, [autoFocus, pendingProduct]);

    const commit = (
        product: ProductPickerItem,
        variation?: ProductPickerVariation | null,
        meta?: {qrWeight?: number; qrPrice?: number},
    ) => {
        onPick({
            product,
            variation: variation || null,
            quantity: meta?.qrWeight && meta.qrWeight > 0 ? meta.qrWeight : 1,
            price: meta?.qrPrice ?? variation?.price ?? getProductPrice(product),
            qrWeight: meta?.qrWeight,
            qrPrice: meta?.qrPrice,
        });
        setQuery('');
        setMessage(`Добавлено: ${product.name}`);
        setPendingProduct(null);
        setPendingVariation(null);
        setPendingMeta({});
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const startPick = async (
        product: ProductPickerItem,
        meta?: {qrWeight?: number; qrPrice?: number},
    ) => {
        if (resolvingProduct) return;

        const fetchOptions = {force: forceProductDetails};
        const showLoading = willFetchProductDetails(product, fetchProductDetails, fetchOptions);
        if (showLoading) {
            setResolvingProduct(product);
            setMessage('Загрузка вариаций…');
        }

        try {
            const resolved = await resolveProductDetails(product, fetchProductDetails, fetchOptions);
            if (resolved.has_variation) {
                setPendingProduct(resolved);
                setPendingVariation(null);
                setPendingMeta(meta || {});
                return;
            }
            commit(resolved, null, meta);
        } finally {
            setResolvingProduct(null);
        }
    };

    const resolveQuery = (raw: string) => {
        const value = raw.trim();
        if (!value) return;

        const qr = parseQrCode(value);
        if (qr.productId) {
            const found = products.find((product) => product.id === Number(qr.productId));
            if (found) {
                startPick(found, {
                    qrWeight: qr.weight ?? undefined,
                    qrPrice: qr.price ?? undefined,
                });
                return;
            }
            setMessage('Товар по QR не найден');
            setQuery('');
            return;
        }

        const exact = products.find((product) =>
            String(product.id) === value
            || (product.part_number || '').toLowerCase() === value.toLowerCase(),
        );
        if (exact) {
            startPick(exact);
            return;
        }

        const soft = products.filter((product) => matchProductQuery(product, value));
        if (soft.length === 1) {
            startPick(soft[0]);
            return;
        }
        if (soft.length > 1) {
            setMessage(`Найдено ${soft.length} — уточните артикул`);
            return;
        }
        setMessage('Товар не найден');
    };

    return (
        <S.Root $compact={compact}>
            <S.SearchShell $focused={focused} $compact={compact} $large={!compact}>
                <S.ScanPulse />
                <S.SearchInput
                    ref={inputRef}
                    value={query}
                    disabled={disabled || loading || Boolean(resolvingProduct)}
                    placeholder={placeholder}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            resolveQuery(query);
                        }
                    }}
                />
            </S.SearchShell>
            <S.Hint $compact={compact}>
                {resolvingProduct
                    ? 'Загрузка вариаций…'
                    : loading
                        ? 'Загрузка каталога…'
                        : message}
            </S.Hint>

            {resolvingProduct && (
                <DetailsLoadingOverlay
                    product={resolvingProduct}
                    message={
                        resolvingProduct.has_variation
                            ? 'Загрузка вариаций…'
                            : 'Загрузка товара…'
                    }
                />
            )}

            {pendingProduct && (
                <VariationPicker
                    asModal
                    product={pendingProduct}
                    selectedId={pendingVariation?.id}
                    onSelect={setPendingVariation}
                    onCancel={() => {
                        setPendingProduct(null);
                        setPendingVariation(null);
                        setPendingMeta({});
                        inputRef.current?.focus();
                    }}
                    onConfirm={() => pendingVariation && commit(pendingProduct, pendingVariation, pendingMeta)}
                    confirmDisabled={!pendingVariation}
                />
            )}
        </S.Root>
    );
};

export default PosScanMode;
